import { cpqAxios } from '@/utils/cpqClient';

function readString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** Extracts a PDF URL from CPQ API responses (direct or wrapped). */
export function extractCpqPdfUrl(value: unknown): string | null {
  const directUrl = readString(value);
  if (directUrl) return directUrl;
  if (!isRecord(value)) return null;

  const directKeys = ['pdfUrl', 'pdf_url', 'url', 'signedUrl', 'signed_url'];
  for (const key of directKeys) {
    const url = readString(value[key]);
    if (url) return url;
  }

  return extractCpqPdfUrl(value.responseObject) ?? extractCpqPdfUrl(value.data);
}

/**
 * Resolves a signed PDF URL for a CPQ block/proposal template.
 * Used by template gallery preview for proposal templates.
 */
export async function fetchCpqTemplatePdfUrl(
  cpqTemplateId: string,
  signal?: AbortSignal,
): Promise<string> {
  const baseUrl = import.meta.env.VITE_CPQ_BACKEND_URL?.trim();
  if (!baseUrl) {
    throw new Error('CPQ backend URL is not configured (VITE_CPQ_BACKEND_URL).');
  }

  const { data } = await cpqAxios.get(`/templatev2/${cpqTemplateId}/pdf-url`, {
    signal,
  });
  const pdfUrl = extractCpqPdfUrl(data);

  if (!pdfUrl) {
    throw new Error('PDF URL endpoint did not return a displayable URL.');
  }

  return pdfUrl;
}
